import React, { useState, useEffect } from 'react';
import { Product, Feature, SubDependency, Component, TestCase, Platform, PlatformItem } from '../../types';
import { apiRequest } from '../../utils/api';
import { showTempMessage, MessageSetter } from '../../utils/messages';
import MilestoneSelector from '../shared/MilestoneSelector';

interface ImportProgress {
  total: number;
  current: number;
  currentItem: string;
  importedFeatures: number;
  importedComponents: number;
  importedTestCases: number;
  importedSubDeps: number; // Legacy
  skipped: number;
  errors: number;
}

interface FeatureImporterProps {
  selectedProduct: string;
  products: Product[];
  features: Feature[];
  onImportComplete: () => Promise<void>;
  setMessage?: MessageSetter;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to strip HTML tags from text
const stripHtmlTags = (text: string | undefined): string => {
  if (!text) return '';
  // Remove HTML tags while preserving the text content
  return text
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&')  // Replace &amp; with &
    .replace(/&lt;/g, '<')   // Replace &lt; with <
    .replace(/&gt;/g, '>')   // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'")  // Replace &#39; with '
    .trim();
};

const FeatureImporter: React.FC<FeatureImporterProps> = ({
  selectedProduct,
  products,
  features,
  onImportComplete,
  setMessage,
  isOpen,
  onClose
}) => {
  const [importText, setImportText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [componentsNeedingPlatforms, setComponentsNeedingPlatforms] = useState<Component[]>([]);
  const [showPlatformSelection, setShowPlatformSelection] = useState(false);
  const [currentPlatformStep, setCurrentPlatformStep] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [availablePlatforms, setAvailablePlatforms] = useState<PlatformItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  
  // Milestone selection state
  const [showMilestoneSelection, setShowMilestoneSelection] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<string>('');

  // Fetch platforms from API, filtered by selected product
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const platformsData = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true });
        // Filter platforms by selected product
        // Show platform if it has no productIds (applies to all) or includes the selected product
        const filterByProduct = (items: PlatformItem[]): PlatformItem[] => {
          if (!selectedProduct) return items;
          return items.filter(item => 
            !item.productIds || item.productIds.length === 0 || item.productIds.includes(selectedProduct)
          );
        };
        const filteredPlatforms = filterByProduct(Array.isArray(platformsData) ? platformsData : []);
        setAvailablePlatforms(filteredPlatforms);
      } catch (error) {
        console.error('Error fetching platforms:', error);
      }
    };
    fetchPlatforms();
  }, [selectedProduct]);


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        if (setMessage) {
          showTempMessage(setMessage, { type: 'error', text: 'Please select a JSON file' });
        }
        return;
      }
      
      setSelectedFile(file);
      setFileName(file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          setImportText(content);
        } catch (error) {
          if (setMessage) {
            showTempMessage(setMessage, { type: 'error', text: 'Failed to read file' });
          }
        }
      };
      reader.onerror = () => {
        if (setMessage) {
          showTempMessage(setMessage, { type: 'error', text: 'Error reading file' });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setFileName('');
    setImportText('');
    // Reset file input
    const fileInput = document.getElementById('json-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Show milestone selection before starting import
  const handleImportClick = () => {
    if (!selectedProduct) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Please select a product' });
      }
      return;
    }

    if (!selectedFile || !importText.trim()) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Please upload a JSON file' });
      }
      return;
    }

    // Validate JSON before showing milestone selection
    try {
      const qaseData = JSON.parse(importText.trim());
      if (!qaseData || !Array.isArray(qaseData.suites)) {
        if (setMessage) {
          showTempMessage(setMessage, { type: 'error', text: 'Invalid JSON format. Expected Qase JSON export with suites array.' });
        }
        return;
      }
    } catch (e) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Failed to parse JSON file. Please ensure it is valid JSON.' });
      }
      return;
    }

    // Show milestone selection modal
    setShowMilestoneSelection(true);
  };

  // Proceed with import after milestone selection
  const handleMilestoneConfirm = async () => {
    if (!selectedMilestone) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Please select a milestone' });
      }
      return;
    }

    setShowMilestoneSelection(false);
    await handleImport();
  };

  const handleImport = async () => {
    // Prevent double-clicking
    if (isImporting) {
      return;
    }

    setIsImporting(true);
    const importedFeatures: Feature[] = [];
    const importedComponents: Component[] = [];
    const importedTestCases: TestCase[] = [];
    const updatedTestCases: TestCase[] = [];
    const importedSubDeps: SubDependency[] = []; // Legacy
    const errors: string[] = [];
    const componentsWithoutPlatforms: Component[] = []; // Track components needing platforms
    let skipped = 0;
    let totalItems = 0;
    let currentItem = 0;

    // Parse JSON file
    let qaseData: any = null;
    
    try {
      qaseData = JSON.parse(importText.trim());
      if (!qaseData || !Array.isArray(qaseData.suites)) {
        if (setMessage) {
          showTempMessage(setMessage, { type: 'error', text: 'Invalid JSON format. Expected Qase JSON export with suites array.' });
        }
        setIsImporting(false);
        return;
      }
    } catch (e) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Failed to parse JSON file. Please ensure it is valid JSON.' });
      }
      setIsImporting(false);
      return;
    }

    if (qaseData) {
      // Qase JSON import
      const selectedProductData = products.find(p => p.id === selectedProduct);
      const projectId = selectedProductData?.projectId || 'FS'; // Default to 'FS' if not set

      // Use the milestone selected by the user
      const importMilestone = selectedMilestone;

      // Count total items first for progress tracking
      // Structure: Top-level suite = Feature, Sub-suite = Component, Cases = TestCase
      const countItems = (suites: any[], isTopLevel: boolean = true): number => {
        let count = 0;
        for (const suite of suites) {
          if (suite.title) {
            if (isTopLevel) {
              count++; // Feature
              // Count feature-level test cases - always create default component if cases exist
              if (Array.isArray(suite.cases) && suite.cases.length > 0) {
                count++; // Default component
                count += suite.cases.length; // Feature-level TestCases
              }
            } else {
              count++; // Component
              if (Array.isArray(suite.cases)) {
                count += suite.cases.length; // TestCases within component
              }
            }
            // Recursively count nested suites (sub-suites become Components)
            if (Array.isArray(suite.suites) && suite.suites.length > 0) {
              count += countItems(suite.suites, false); // Sub-suites are Components
            }
          }
        }
        return count;
      };

      totalItems = countItems(qaseData.suites, true);
      setImportProgress({
        total: totalItems,
        current: 0,
        currentItem: 'Starting import...',
        importedFeatures: 0,
        importedComponents: 0,
        importedTestCases: 0,
        importedSubDeps: 0,
        skipped: 0,
        errors: 0
      });

      // Fetch all existing components and test cases for duplicate checking
      const [allExistingComponentsData, allExistingTestCasesData] = await Promise.all([
        apiRequest<Component[]>('/api/components', {}, { useAuth: true }).catch(() => []),
        apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true }).catch(() => [])
      ]);
      const allExistingComponents: Component[] = Array.isArray(allExistingComponentsData) ? allExistingComponentsData : [];
      const allExistingTestCases: TestCase[] = Array.isArray(allExistingTestCasesData) ? allExistingTestCasesData : [];

      // Helper to create Qase suite URL
      const createQaseSuiteUrl = (suiteId: number): string => {
        return `https://app.qase.io/suite/${projectId}-${suiteId}`;
      };

      // Process top-level suites (Features) and sub-suites (Components)
      const processTopLevelSuite = async (suite: any) => {
        if (!suite.title) return;

        currentItem++;
        setImportProgress({
          total: totalItems,
          current: currentItem,
          currentItem: `Processing feature: ${suite.title}`,
          importedFeatures: importedFeatures.length,
          importedComponents: importedComponents.length,
          importedTestCases: importedTestCases.length,
          importedSubDeps: importedSubDeps.length,
          skipped: skipped,
          errors: errors.length
        });

        // Create or find feature (top-level suite)
        let feature = features.find(f => f.name === suite.title);
        if (!feature) {
          try {
            const newFeature = await apiRequest<Feature>('/api/features', {
              method: 'POST',
              body: JSON.stringify({
                name: suite.title,
                productId: selectedProduct,
                description: suite.description || undefined
              })
            }, { useAuth: true });
            feature = newFeature;
            if (feature) {
              importedFeatures.push(feature);
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            errors.push(`Failed to create feature "${suite.title}": ${errorMsg}`);
            return;
          }
        }

        // If feature has test cases at suite level, create a default component for them
        // This handles cases where a suite has both direct test cases AND sub-suites
        if (Array.isArray(suite.cases) && suite.cases.length > 0 && feature) {
          currentItem++;
          setImportProgress({
            total: totalItems,
            current: currentItem,
            currentItem: `Creating default component for: ${suite.title}`,
            importedFeatures: importedFeatures.length,
            importedComponents: importedComponents.length,
            importedTestCases: importedTestCases.length,
            importedSubDeps: importedSubDeps.length,
            skipped: skipped,
            errors: errors.length
          });

          try {
            // Check if default component already exists
            const existingComponentsForFeature = allExistingComponents.filter(c => c.featureId === feature!.id);
            const defaultComponentName = `${suite.title} - Default`;
            const duplicateByName = existingComponentsForFeature.some((c: Component) => 
              c.name.toLowerCase() === defaultComponentName.toLowerCase()
            );

            let defaultComponent: Component | null = null;
            if (duplicateByName) {
              // Use existing default component
              defaultComponent = existingComponentsForFeature.find(c => 
                c.name.toLowerCase() === defaultComponentName.toLowerCase()
              ) || null;
            } else {
              // Create default component
              defaultComponent = await apiRequest<Component>('/api/components', {
                method: 'POST',
                body: JSON.stringify({
                  name: defaultComponentName,
                  featureId: feature.id,
                  description: `Default component for ${suite.title}`,
                  milestone: importMilestone
                })
              }, { useAuth: true });
              
              if (defaultComponent) {
                importedComponents.push(defaultComponent);
                allExistingComponents.push(defaultComponent);
                // Track if component doesn't have platforms
                if (!defaultComponent.platforms || defaultComponent.platforms.length === 0) {
                  componentsWithoutPlatforms.push(defaultComponent);
                }
              }
            }

            // Process test cases within the default component
            if (defaultComponent) {
              await processTestCases(suite.cases, defaultComponent.id);
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            errors.push(`Failed to create default component for "${suite.title}": ${errorMsg}`);
          }
        }

        // Process sub-suites (Components) - these are the nested suites
        if (Array.isArray(suite.suites) && suite.suites.length > 0 && feature) {
          for (const subSuite of suite.suites) {
            if (!subSuite.title) continue;

            currentItem++;
            setImportProgress({
              total: totalItems,
              current: currentItem,
              currentItem: `Processing component: ${subSuite.title}`,
              importedFeatures: importedFeatures.length,
              importedComponents: importedComponents.length,
              importedTestCases: importedTestCases.length,
              importedSubDeps: importedSubDeps.length,
              skipped: skipped,
              errors: errors.length
            });

            try {
              // Create Qase suite URL if suite ID exists
              const qaseSuiteId = subSuite.id ? createQaseSuiteUrl(subSuite.id) : undefined;

              // Check for duplicates: by name within feature
              const existingComponentsForFeature = allExistingComponents.filter(c => c.featureId === feature!.id);
              const duplicateByName = existingComponentsForFeature.some((c: Component) => 
                c.name.toLowerCase() === subSuite.title.toLowerCase()
              );

              if (duplicateByName) {
                skipped++;
                // Still process test cases for existing component
                const existingComponent = existingComponentsForFeature.find(c => 
                  c.name.toLowerCase() === subSuite.title.toLowerCase()
                );
                if (existingComponent && Array.isArray(subSuite.cases)) {
                  await processTestCases(subSuite.cases, existingComponent.id);
                }
                continue;
              }

              const newComponent = await apiRequest<Component>('/api/components', {
                method: 'POST',
                body: JSON.stringify({
                  name: subSuite.title,
                  featureId: feature.id,
                  description: subSuite.description || undefined,
                  qaseSuiteId: qaseSuiteId,
                  milestone: importMilestone
                  // Platforms are ExoTest-only, not imported from Qase
                })
              }, { useAuth: true });
              
              importedComponents.push(newComponent);
              allExistingComponents.push(newComponent);
              // Track if component doesn't have platforms
              if (!newComponent.platforms || newComponent.platforms.length === 0) {
                componentsWithoutPlatforms.push(newComponent);
              }

              // Process test cases within this component
              if (Array.isArray(subSuite.cases)) {
                await processTestCases(subSuite.cases, newComponent.id);
              }
            } catch (error) {
              const errorMsg = error instanceof Error ? error.message : 'Unknown error';
              errors.push(`Failed to create component "${subSuite.title}" for "${suite.title}": ${errorMsg}`);
            }
          }
        }
      };

      // Process test cases within a component (upsert: update if exists, create if not)
      const processTestCases = async (cases: any[], componentId: string | null) => {
        for (const testCase of cases) {
          if (!testCase.title) continue;

          // Strip HTML tags from title
          const cleanTitle = stripHtmlTags(testCase.title);
          if (!cleanTitle) continue;

          currentItem++;
          setImportProgress({
            total: totalItems,
            current: currentItem,
            currentItem: `Processing test case: ${cleanTitle}`,
            importedFeatures: importedFeatures.length,
            importedComponents: importedComponents.length,
            importedTestCases: importedTestCases.length,
            importedSubDeps: importedSubDeps.length,
            skipped: skipped,
            errors: errors.length
          });

          try {
            // Strip HTML from description and preconditions
            const cleanDescription = stripHtmlTags(testCase.description);
            const cleanPreConditions = stripHtmlTags(testCase.preconditions);

            // Extract steps from Qase test case and strip HTML from each field
            // Qase exports steps as array with action, expected_result, and position
            const steps = Array.isArray(testCase.steps) ? testCase.steps.map((step: { action?: string; expected_result?: string; data?: string; position?: number }, index: number) => ({
              id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              action: stripHtmlTags(step.action) || '',
              data: stripHtmlTags(step.data) || undefined,
              expectedResult: stripHtmlTags(step.expected_result) || undefined,
              order: step.position || index + 1
            })).filter((step: { action: string }) => step.action.trim()) : undefined;

            // Check if test case with same title already exists in this component (for upsert)
            const existingTestCase = componentId ? allExistingTestCases.find((tc: TestCase) => 
              tc.componentId === componentId && 
              tc.title.toLowerCase().trim() === cleanTitle.toLowerCase().trim()
            ) : null;

            if (existingTestCase) {
              // UPDATE existing test case
              const updated = await apiRequest<TestCase>(`/api/testcases/${existingTestCase.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                  title: cleanTitle,
                  componentId: componentId || undefined,
                  description: cleanDescription || undefined,
                  preConditions: cleanPreConditions || undefined,
                  steps: steps && steps.length > 0 ? steps : existingTestCase.steps
                  // Keep existing testCaseNumber, automated status, etc.
                })
              }, { useAuth: true });
              
              updatedTestCases.push(updated);
              // Update in allExistingTestCases array
              const idx = allExistingTestCases.findIndex((tc: TestCase) => tc.id === existingTestCase.id);
              if (idx !== -1) {
                allExistingTestCases[idx] = updated;
              }
            } else {
              // CREATE new test case (no qaseId stored anymore)
              const newTestCase = await apiRequest<TestCase>('/api/testcases', {
                method: 'POST',
                body: JSON.stringify({
                  title: cleanTitle,
                  componentId: componentId || undefined,
                  description: cleanDescription || undefined,
                  preConditions: cleanPreConditions || undefined,
                  steps: steps && steps.length > 0 ? steps : undefined
                })
              }, { useAuth: true });
              
              importedTestCases.push(newTestCase);
              allExistingTestCases.push(newTestCase);
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            errors.push(`Failed to process test case "${cleanTitle}": ${errorMsg}`);
          }
        }
      };

      // Process all top-level suites (Features)
      for (const suite of qaseData.suites) {
        await processTopLevelSuite(suite);
      }
    }

    await onImportComplete();

    // Update final progress
    setImportProgress({
      total: totalItems,
      current: totalItems,
      currentItem: 'Import complete!',
      importedFeatures: importedFeatures.length,
      importedComponents: importedComponents.length,
      importedTestCases: importedTestCases.length,
      importedSubDeps: importedSubDeps.length,
      skipped: skipped,
      errors: errors.length
    });

    // Close progress modal after a brief delay to show final results
    setTimeout(async () => {
      setIsImporting(false);
      setImportProgress(null);
      
      const parts: string[] = [];
      if (importedFeatures.length > 0) parts.push(`${importedFeatures.length} feature${importedFeatures.length !== 1 ? 's' : ''}`);
      if (importedComponents.length > 0) parts.push(`${importedComponents.length} component${importedComponents.length !== 1 ? 's' : ''}`);
      if (importedTestCases.length > 0) parts.push(`${importedTestCases.length} test case${importedTestCases.length !== 1 ? 's' : ''}`);
      if (updatedTestCases.length > 0) parts.push(`${updatedTestCases.length} test case${updatedTestCases.length !== 1 ? 's' : ''} updated`);
      if (importedSubDeps.length > 0) parts.push(`${importedSubDeps.length} sub-dependenc${importedSubDeps.length !== 1 ? 'ies' : 'y'}`);
      
      const successMsg = `Imported ${parts.join(', ')}.${skipped > 0 ? ` Skipped ${skipped} duplicate${skipped !== 1 ? 's' : ''}.` : ''}`;
      if (setMessage) {
        if (errors.length > 0) {
          showTempMessage(setMessage, { 
            type: 'error', 
            text: `${successMsg}\n\nErrors:\n${errors.join('\n')}` 
          });
        } else {
          showTempMessage(setMessage, { type: 'success', text: successMsg });
        }
      }
      
      // If there are components needing platforms, check for auto-assignment
      if (componentsWithoutPlatforms.length > 0) {
        // Filter platforms by selected product
        const filteredPlatforms = availablePlatforms.filter(platform => 
          !platform.productIds || platform.productIds.length === 0 || platform.productIds.includes(selectedProduct)
        );
        
        // Auto-assign if only 1 platform exists for this product
        if (filteredPlatforms.length === 1) {
          const singlePlatform = filteredPlatforms[0].name as Platform;
          
          // Auto-assign the single platform to all components
          try {
            for (const component of componentsWithoutPlatforms) {
              try {
                await apiRequest<Component>(`/api/components/${component.id}`, {
                  method: 'PUT',
                  body: JSON.stringify({
                    platforms: [singlePlatform]
                  })
                }, { useAuth: true });
              } catch (error) {
                console.error(`Failed to auto-assign platform to ${component.name}:`, error);
                if (setMessage) {
                  showTempMessage(setMessage, { 
                    type: 'error', 
                    text: `Failed to auto-assign platform to ${component.name}` 
                  });
                }
              }
            }
            await onImportComplete();
            onClose();
            setImportText('');
            setSelectedFile(null);
            setFileName('');
            const fileInput = document.getElementById('json-file-input') as HTMLInputElement;
            if (fileInput) {
              fileInput.value = '';
            }
            return; // Exit early since we're done
          } catch (error) {
            console.error('Error during auto-assignment:', error);
            // Fall through to show platform selection modal if auto-assignment fails
          }
        }
        
        // Show platform selection modal if we didn't auto-assign
        setComponentsNeedingPlatforms(componentsWithoutPlatforms);
        setCurrentPlatformStep(0);
        setSelectedPlatforms([]);
        setShowPlatformSelection(true);
      } else {
        onClose();
        setImportText('');
        setSelectedFile(null);
        setFileName('');
        // Reset file input
        const fileInput = document.getElementById('json-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }, 1000);
  };

  const handlePlatformNext = async () => {
    const currentComponent = componentsNeedingPlatforms[currentPlatformStep];
    if (currentComponent && selectedPlatforms.length > 0) {
      try {
        await apiRequest<Component>(`/api/components/${currentComponent.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            platforms: selectedPlatforms
          })
        }, { useAuth: true });
      } catch (error) {
        console.error('Failed to update component platforms:', error);
        if (setMessage) {
          showTempMessage(setMessage, { 
            type: 'error', 
            text: `Failed to update platforms for ${currentComponent.name}` 
          });
        }
      }
    }
    
    if (currentPlatformStep < componentsNeedingPlatforms.length - 1) {
      setCurrentPlatformStep(currentPlatformStep + 1);
      setSelectedPlatforms([]);
    } else {
      // Finished all steps - refresh component list to show updated platforms
      setShowPlatformSelection(false);
      setComponentsNeedingPlatforms([]);
      await onImportComplete(); // Refresh components list to show updated platforms
      onClose();
      setImportText('');
      setSelectedFile(null);
      setFileName('');
      // Reset file input
      const fileInput = document.getElementById('json-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handlePlatformSkipAll = async () => {
    // Skip all remaining components and close the modal
    setShowPlatformSelection(false);
    setComponentsNeedingPlatforms([]);
    await onImportComplete(); // Refresh components list
    onClose();
    setImportText('');
    setSelectedFile(null);
    setFileName('');
    // Reset file input
    const fileInput = document.getElementById('json-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const togglePlatform = (platform: Platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  // Get filtered platforms for the selected product
  const filteredPlatforms = availablePlatforms.filter(platform => 
    !selectedProduct || !platform.productIds || platform.productIds.length === 0 || platform.productIds.includes(selectedProduct)
  );

  // Auto-select platform when modal opens if only 1 platform exists
  useEffect(() => {
    if (showPlatformSelection && filteredPlatforms.length === 1 && selectedPlatforms.length === 0) {
      const singlePlatform = filteredPlatforms[0].name as Platform;
      setSelectedPlatforms([singlePlatform]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlatformSelection, filteredPlatforms.length]);

  if (!isOpen) return null;

  return (
    <>
      {/* Platform Selection Modal */}
      {showPlatformSelection && componentsNeedingPlatforms.length > 0 && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click during platform selection
          e.stopPropagation();
        }}>
          <div className="card modal-content import-modal" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-md">
              <h2 className="title-lg">Select Platforms for Components</h2>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={handlePlatformSkipAll}
              >
                ✕
              </button>
            </div>
            
            <div className="mb-md">
              <div className="alert alert-info">
                <div>
                  <strong>Step {currentPlatformStep + 1} of {componentsNeedingPlatforms.length}</strong>
                  <br />
                  <span className="hint">Select platforms for this component (optional). You can skip this step.</span>
                </div>
              </div>
            </div>

            {componentsNeedingPlatforms[currentPlatformStep] && (
              <>
                <div className="mb-md">
                  <label className="label">Component: {componentsNeedingPlatforms[currentPlatformStep].name}</label>
                  {componentsNeedingPlatforms[currentPlatformStep].description && (
                    <div className="hint mb-sm">{componentsNeedingPlatforms[currentPlatformStep].description}</div>
                  )}
                </div>

                <div className="mb-md">
                  <label className="label">Platforms (Optional)</label>
                  {filteredPlatforms.length === 0 ? (
                    <div style={{ fontSize: '0.875rem', color: '#999', fontStyle: 'italic' }}>
                      No platforms available for this product. Please add platforms in Settings.
                    </div>
                  ) : (
                    <>
                      <div className="platform-controls-group-runner">
                        {filteredPlatforms.map(platformItem => {
                          const platform = platformItem.name as Platform;
                          const isSelected = selectedPlatforms.includes(platform);
                          return (
                            <div key={platformItem.id} className="platform-control-wrapper-runner">
                              <button
                                type="button"
                                className={`platform-tested-button ${isSelected ? 'tested' : ''}`}
                                onClick={() => togglePlatform(platform)}
                                title={isSelected ? `${platform} selected (click to deselect)` : `Select ${platform}`}
                              >
                                <span className="platform-name">{platform}</span>
                                {isSelected && <span className="platform-icon">✓</span>}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                        {filteredPlatforms.length === 1 
                          ? 'Only one platform available for this product. It has been auto-selected.'
                          : 'Select platforms this component tests on. Used for insights and platform coverage metrics.'}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-md mt-md">
              <button 
                className="btn btn-primary btn-lg" 
                onClick={handlePlatformNext}
              >
                {currentPlatformStep < componentsNeedingPlatforms.length - 1 ? 'Next' : 'Finish'}
              </button>
              <button 
                className="btn btn-secondary btn-lg" 
                onClick={handlePlatformSkipAll}
              >
                Skip All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {!showPlatformSelection && (
        <div className="modal-overlay">
          <div className="card modal-content import-modal" onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-md">
            <h2 className="title-lg">Import Features & Components</h2>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              disabled={isImporting}
            >
              ✕
            </button>
          </div>
          
          <p className="subtitle mb-md">
            Upload a Qase JSON export file to import features, components, and test cases.
          </p>
          <div className="mb-md">
            <div className="alert alert-magenta">
              <div>
                <strong>Qase JSON File:</strong> The importer will extract suites as features, sub-suites as components, and test cases with their steps.
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="mb-md">
            <label className="label">Upload JSON File</label>
            <div className="flex gap-sm items-center">
              <input
                id="json-file-input"
                type="file"
                accept=".json,application/json"
                onChange={handleFileSelect}
                disabled={isImporting}
                style={{ display: 'none' }}
              />
              <label htmlFor="json-file-input">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isImporting}
                  onClick={() => document.getElementById('json-file-input')?.click()}
                >
                  {fileName ? 'Change File' : 'Choose JSON File'}
                </button>
              </label>
              {fileName && (
                <div className="flex items-center gap-sm">
                  <span className="hint">{fileName}</span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleClearFile}
                    disabled={isImporting}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-md mt-md">
            <button 
              className="btn btn-success btn-lg" 
              onClick={handleImportClick}
              disabled={isImporting}
            >
              {isImporting ? '⏳ Importing...' : '✓ Import'}
            </button>
            <button 
              className="btn btn-secondary btn-lg" 
              onClick={onClose}
              disabled={isImporting}
            >
              Cancel
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Milestone Selection Modal */}
      {showMilestoneSelection && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="card modal-content import-modal" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-md">
              <h2 className="title-lg">Select Milestone for Import</h2>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setShowMilestoneSelection(false);
                  setSelectedMilestone('');
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="mb-md">
              <div className="alert alert-info">
                <div>
                  <strong>All imported components will be assigned to this milestone.</strong>
                  <br />
                  <span className="hint">Select an existing milestone or create a new one.</span>
                </div>
              </div>
            </div>

            <div className="mb-md">
              <label className="label">Milestone</label>
              <MilestoneSelector
                value={selectedMilestone}
                onChange={setSelectedMilestone}
                productId={selectedProduct}
                placeholder="Select or create a milestone..."
                required={true}
              />
            </div>

            <div className="flex gap-md mt-md">
              <button 
                className="btn btn-success btn-lg" 
                onClick={handleMilestoneConfirm}
                disabled={!selectedMilestone}
              >
                ✓ Continue Import
              </button>
              <button 
                className="btn btn-secondary btn-lg" 
                onClick={() => {
                  setShowMilestoneSelection(false);
                  setSelectedMilestone('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Progress Modal */}
      {isImporting && importProgress && (
        <div className="modal-overlay import-progress-overlay">
          <div className="card modal-content import-progress-modal">
            <h2 className="import-progress-title">
              Importing Features & Sub-dependencies
            </h2>
            
            {/* Progress Bar */}
            <div 
              className="import-progress-bar-container"
              style={{ '--progress-percent': importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0 } as React.CSSProperties}
            >
              <div className="import-progress-bar-fill">
                {importProgress.total > 0 ? `${Math.round((importProgress.current / importProgress.total) * 100)}%` : '0%'}
              </div>
            </div>

            {/* Current Item */}
            <div className="import-progress-current-item">
              <strong>Processing:</strong> {importProgress.currentItem}
            </div>

            {/* Statistics */}
            <div className="import-progress-stats">
              <div className="import-stat-card import-stat-success">
                <div className="import-stat-value text-success">
                  {importProgress.importedFeatures}
                </div>
                <div className="import-stat-label">
                  Features
                </div>
              </div>
              {importProgress.importedComponents > 0 && (
                <div className="import-stat-card import-stat-info">
                  <div className="import-stat-value text-cyan">
                    {importProgress.importedComponents}
                  </div>
                  <div className="import-stat-label">
                    Components
                  </div>
                </div>
              )}
              {importProgress.importedTestCases > 0 && (
                <div className="import-stat-card import-stat-info">
                  <div className="import-stat-value text-cyan">
                    {importProgress.importedTestCases}
                  </div>
                  <div className="import-stat-label">
                    Test Cases
                  </div>
                </div>
              )}
              {importProgress.importedSubDeps > 0 && (
              <div className="import-stat-card import-stat-info">
                <div className="import-stat-value text-cyan">
                  {importProgress.importedSubDeps}
                </div>
                <div className="import-stat-label">
                    Sub-deps (legacy)
                  </div>
                </div>
              )}
              {importProgress.skipped > 0 && (
                <div className="import-stat-card import-stat-warning">
                  <div className="import-stat-value text-warning">
                    {importProgress.skipped}
                  </div>
                  <div className="import-stat-label">
                    Skipped (duplicates)
                  </div>
                </div>
              )}
              {importProgress.errors > 0 && (
                <div className="import-stat-card import-stat-error">
                  <div className="import-stat-value text-danger">
                    {importProgress.errors}
                  </div>
                  <div className="import-stat-label">
                    Errors
                  </div>
                </div>
              )}
            </div>

            {/* Progress Text */}
            <div className="import-progress-text">
              {importProgress.current} of {importProgress.total} items processed
            </div>

            <div className="import-progress-hint">
              Please wait while the import completes...
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeatureImporter;

